/**
 * InternalSection — Card mit Feldern die NUR in StayOn leben (Sprint-16, Mockup 4e).
 *
 * Enthaelt:
 *   - Bedrooms/Bathrooms:      Cleaning-Aufwand + Checklist-Skalierung (NumberStepper)
 *   - CheckInTime/CheckOutTime: Cleaning-Slot + Phase-2-Onboarding
 *   - CleaningFee:              Invoice-Zeile + Housekeeper-Payout
 *   - Description:              Interne Notizen
 *
 * Hinweis am Header: Werte werden NICHT zu OTAs gesynct — die OTAs pflegen
 * ihre eigenen Bedroom-/Bath-/Check-In-Counts.
 */
import { Controller, type Control, type FieldErrors, type UseFormRegister } from 'react-hook-form';
import { Lock, Sparkles } from 'lucide-react';
import { FormField, Input, NumberStepper, Textarea } from '@/shared/components';
import type { PropertyValues } from './schema';
import { PropertyFormSectionHeader } from './ChannelsPushedSection';

interface InternalSectionProps {
  register: UseFormRegister<PropertyValues>;
  control: Control<PropertyValues>;
  errors: FieldErrors<PropertyValues>;
}

export function InternalSection({ register, control, errors }: InternalSectionProps) {
  return (
    <div className="bg-card border-line/50 shadow-card rounded-lg border p-6">
      <PropertyFormSectionHeader
        icon={<Lock size={13} strokeWidth={2} />}
        iconTint="paper"
        title="Internal"
        subtitle="Nur intern sichtbar — wird nicht an Channels gepusht."
      />

      <div className="grid gap-4 md:grid-cols-2">
        <FormField
          label="Interner Kurzname"
          error={errors.operationalName?.message}
          hint="Nur in StayOn für das Team; öffentliche Namen und Channels bleiben unverändert."
          className="md:col-span-2"
        >
          <Input
            {...register('operationalName')}
            maxLength={80}
            placeholder="z. B. PW6 · 2. OG links"
          />
        </FormField>
        <FormField label="Schlafzimmer" error={errors.bedrooms?.message} required>
          <Controller
            control={control}
            name="bedrooms"
            render={({ field }) => (
              <NumberStepper
                value={Number(field.value) || 0}
                onChange={field.onChange}
                min={0}
                max={20}
              />
            )}
          />
        </FormField>
        <FormField label="Bäder" error={errors.bathrooms?.message} required>
          <Controller
            control={control}
            name="bathrooms"
            render={({ field }) => (
              <NumberStepper
                value={Number(field.value) || 0}
                onChange={field.onChange}
                min={0}
                max={10}
                step={0.5}
              />
            )}
          />
        </FormField>

        {/*
          Check-in/-out gehoeren dem Objekt (I2): alle Apartments erben sie, und der Kanalmanager
          kennt je Objekt-Property ohnehin nur einen Wert. Hier stand ein Eingabefeld, das eine
          Differenzierung versprach, die nirgends ankommt.
        */}

        <FormField
          label="Cleaning-Fee"
          error={errors.cleaningFee?.message}
          hint="Eigene Rechnungs-Zeile + Housekeeper-Payout."
          required
          className="md:col-span-2"
        >
          <Input
            type="number"
            min={0}
            step={5}
            {...register('cleaningFee')}
            leftIcon={<Sparkles size={14} strokeWidth={2} />}
            placeholder="45"
          />
        </FormField>

        <FormField
          label="Notizen"
          error={errors.description?.message}
          hint="Nur fürs Team. Listing-Text pflegst du in Airbnb/Booking direkt."
          className="md:col-span-2"
        >
          <Textarea
            {...register('description')}
            rows={3}
            placeholder="Interne Notizen, optional."
          />
        </FormField>
      </div>
    </div>
  );
}
